import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import styles from "./Sales.module.css";
import { FaSearch, FaTimesCircle } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import AddSaleOrderPanel from "./AddSaleOrderPanel";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../Services/Store";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import BulkConfirmModal, { BulkAction, isEstimateStatus, isPlacedStatus } from "./BulkConfirmModal";

const PAGE_SIZE = 20;

// --- Types ---
interface SaleOrder {
  sale_order_id: number | string;
  order_no?: string;
  customer_id: string | number;
  order_date?: string;
  created_at?: string;
  username?: string;
  company_name?: string;
  paid_amount?: number | string;
  final_amount?: number | string;
  due_amount?: number | string;
  payment_image?: string;
  status: string;
  status_label?: string;
  [key: string]: any;
}

interface Pagination {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

interface Employee {
  user_id: number;
  username: string;
  mobile_number: string;
  role_id: number;
  role_status: string;
  role_type: string;
  [key: string]: any;
}

const ORDER_PROGRESS_STEPS = [
  "CHECKOUT",
  "PLACED",
  "PENDING",
  "IN_PROCESSING",
  "SHIPPED",
  "COMPLETED"
] as const;

const STATUS_LABELS: Record<string, string> = {
  ALL: "All",
  CHECKOUT: "Checkout",
  PLACED: "Placed",
  PENDING: "Pending",
  PENDING_PAYMENT: "Pending Payment",
  PAYMENT_SUBMITTED: "Payment Submitted",
  IN_PROCESSING: "In Processing",
  SHIPPED: "Shipped",
  COMPLETED: "Completed",
  CANCELED: "Canceled",
  REJECTED: "Rejected"
};

const STATUS_COLORS: Record<string, string> = {
  CHECKOUT: "#D1D5DB",
  PLACED: "#FFD400",
  PENDING: "#FFA600",
  PENDING_PAYMENT: "#FFA600",
  PAYMENT_SUBMITTED: "#FFD400",
  IN_PROCESSING: "#7FB5FF",
  SHIPPED: "#7FB5FF",
  COMPLETED: "#10973b",
  CANCELED: "#F04848",
  REJECTED: "#AD1515",
  default: "#DDD"
};

const STATUS_TABS = [
  "ALL",
  ...ORDER_PROGRESS_STEPS,
  "CANCELED",
  "REJECTED"
];

const isProgressStatus = (status: string) =>
  ORDER_PROGRESS_STEPS.includes(status as any);

const currency = (amt: number | string | undefined) => {
  const val = typeof amt === "string" ? parseFloat(amt) : amt;
  return typeof val === "number" && !isNaN(val)
    ? `₹${val.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "-";
};

function formatCreatedAt(dateStr?: string): string {
  if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function displayCustomerName(order: SaleOrder): string {
  const companyName = order.company_name?.trim();
  const userName = order.username?.trim();
  
  if (companyName) {
    return userName ? `${companyName} (${userName})` : companyName;
  }
  return userName || String(order.customer_id);
}

const Sales: React.FC = () => {
  useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [selectedOrderId, setSelectedOrderId] = useState<string | number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | string | null>(null);
  const [openedFromProceedToPay, setOpenedFromProceedToPay] = useState(false);


  // --- Tab Persistence via localStorage ---
  const TAB_KEY = "sales_active_tab";
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<string>(() => {
    const persistedTab = localStorage.getItem(TAB_KEY);
    return persistedTab && STATUS_TABS.includes(persistedTab) ? persistedTab : STATUS_TABS[0];
  });
  useEffect(() => { localStorage.setItem(TAB_KEY, tab); }, [tab]);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef(false);

  const [orders, setOrders] = useState<SaleOrder[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [apiError, setApiError] = useState("");

  // Date filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- Assign Employee Popup State
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [assigningOrder, setAssigningOrder] = useState<SaleOrder | null>(null);
  const [, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [specialNote, setSpecialNote] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [mobileSearch, setMobileSearch] = useState("");
  const [employeePickerOpen, setEmployeePickerOpen] = useState(true);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // --- Bulk confirm (several orders of a client at once)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);

  // --- Fetch Orders (append=false replaces list; append=true loads next page)
  const fetchOrders = useCallback(async (pageToLoad = 1, append = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setHasMore(true);
      if (pageToLoad === 1) {
        setOrders([]);
        setSelectedIds(new Set());
      }
    }
    setApiError("");
    try {
      const query = [
        `page_number=${pageToLoad}`,
        `page_size=${PAGE_SIZE}`,
        `status=${tab}`,
        search ? `user_like=${encodeURIComponent(search)}` : "",
        mobileSearch ? `mobile_no_like=${encodeURIComponent(mobileSearch)}` : "",
        orderIdSearch ? `order_id_like=${encodeURIComponent(orderIdSearch)}` : "",
        startDate ? `from_date=${startDate}` : "",
        endDate ? `to_date=${endDate}` : "",
      ]
        .filter(Boolean)
        .join("&");
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/saleorders?${query}`,
      });
      const data = res?.data;
      const incoming: SaleOrder[] = Array.isArray(data?.sale_orders) ? data.sale_orders : [];
      const pag: Pagination | null = data?.pagination || null;
      setPagination(pag);
      setPage(pageToLoad);
      if (append) {
        setOrders((prev) => {
          const seen = new Set(prev.map((o) => String(o.sale_order_id)));
          return [...prev, ...incoming.filter((o) => !seen.has(String(o.sale_order_id)))];
        });
      } else {
        setOrders(incoming);
      }
      const totalPages = pag?.total_pages ?? 1;
      setHasMore(pageToLoad < totalPages && incoming.length > 0);
    } catch {
      if (!append) {
        setOrders([]);
        setPagination(null);
      }
      setHasMore(false);
      setApiError("Failed to load sale orders. Please try again later.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [tab, search, mobileSearch, orderIdSearch, startDate, endDate]);

  const refreshOrders = useCallback(() => {
    fetchOrders(1, false);
  }, [fetchOrders]);

  // --- Fetch Employees on open assign popup
// REPLACE the whole function with this:
const fetchEmployees = async (nameQuery: string = "") => {
  setEmployeeLoading(true);
  try {
    const q = nameQuery.trim();
    const params = new URLSearchParams({
      page_number: "1",
      limit: "100",
    });
    if (q) params.set("name", q);
    const res: any = await WebService.getAPI({
      action: `/v1/door-management/employees?${params.toString()}`,
    });
    const data = Array.isArray(res?.data?.employees) ? res.data.employees : [];
    setEmployees(data);
    setFilteredEmployees(data);
  } catch {
    setEmployees([]);
    setFilteredEmployees([]);
  } finally {
    setEmployeeLoading(false);
  }
};

  // Reset and reload when filters/tab change (or add panel closes/opens refresh)
  useEffect(() => {
    fetchOrders(1, false);
  }, [fetchOrders, showAddPanel]);

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          fetchOrders(page + 1, true);
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchOrders]);

  useEffect(() => {
    document.body.style.overflow = showAddPanel || showAssignPopup ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showAddPanel, showAssignPopup]);

  useEffect(() => {
    if (!showAddPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // setShowAddPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddPanel]);

useEffect(() => {
  if (!showAssignPopup) return;
  const id = setTimeout(() => {
    fetchEmployees(employeeSearch);
  }, 300); // debounce
  return () => clearTimeout(id);
}, [employeeSearch, showAssignPopup]);

  const handleAccept = (order: SaleOrder) => {
    setAssigningOrder(order);
    setShowAssignPopup(true);
    setSelectedEmployeeId(null);
    setSelectedEmployee(null);
    setSpecialNote("");
    setEmployeePickerOpen(true);
    setEmployeeSearch("");
    fetchEmployees();
  };

  const handleAssignSubmit = async () => {
    if (!assigningOrder || !selectedEmployeeId) {
      toast.error("Please select an employee.");
      return;
    }
    setAssignLoading(true);
    try {
      const res: any = await WebService.putAPI({
        action: `/v1/door-management/saleorders/accept-reject`,
        body: {
          sale_order_id: Number(assigningOrder.sale_order_id),
          status: "IN_PROCESSING",
          assigned_to: Number(selectedEmployeeId),
          notes: specialNote || "",
        },
      });
      const taskId = res?.data?.task_id ?? res?.task_id;
      toast.success(
        taskId
          ? `Order accepted and moved to Pending. Task #${taskId} assigned to ${selectedEmployee?.username || "employee"}.`
          : "Order accepted and moved to Pending!"
      );
      setShowAssignPopup(false);
      setAssigningOrder(null);
      setSelectedEmployeeId(null);
      setSelectedEmployee(null);
      setSpecialNote("");
      // Accepted orders wait in Pending until the expected delivery date is entered
      setTab("PENDING");
    } catch (e: any) {
      toast.error(
        typeof e === "string"
          ? e
          : e?.message || "Failed to assign order."
      );
    }
    setAssignLoading(false);
  };

  const handleReject = async (order: SaleOrder) => {
    try {
      await WebService.putAPI({
        action: `/v1/door-management/saleorders/accept-reject`,
        body: {
          sale_order_id: order.sale_order_id,
          status: "REJECTED",
          notes: "",
        },
      });
      toast.success("Order rejected successfully!");
      refreshOrders();
    } catch (err) {
      // toast.error("Failed to reject order");
    }
  };

  const isSelectable = (order: SaleOrder) =>
    isEstimateStatus(order.status) || isPlacedStatus(order.status);
  const selectableOrders = orders.filter(isSelectable);
  const selectedOrders = orders.filter((o) => selectedIds.has(String(o.sale_order_id)));
  const selectedEstimateIds = selectedOrders.filter((o) => isEstimateStatus(o.status)).map((o) => o.sale_order_id);
  const selectedPlacedIds = selectedOrders.filter((o) => isPlacedStatus(o.status)).map((o) => o.sale_order_id);
  const selectedClientCount = new Set(selectedOrders.map((o) => String(o.user_id ?? o.customer_id))).size;
  const allSelectableChecked =
    selectableOrders.length > 0 && selectableOrders.every((o) => selectedIds.has(String(o.sale_order_id)));

  const toggleSelected = (order: SaleOrder) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(order.sale_order_id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      allSelectableChecked ? new Set() : new Set(selectableOrders.map((o) => String(o.sale_order_id)))
    );
  };

  const openProceedToPay = (order: SaleOrder) => {
    setSelectedOrderId(order.sale_order_id);
    setSelectedUserId(order.user_id);
    setOpenedFromProceedToPay(true);
    setShowAddPanel(true);
  };

  const handleReApprove = (order: SaleOrder) => {
    Swal.fire({
      title: "Re-Approve this order?",
      text: "If you re-approve this order, you will not be able to cancel this order.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Re-Approve",
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#eee"
    }).then((result) => {
      if (result.isConfirmed) {
        WebService.putAPI({
          action: `/v1/door-management/saleorders/${order.sale_order_id}/status/PLACED`,
        }).then(() => {
          toast.success("Order re-approved!");
          refreshOrders();
        }).catch(() => {
          toast.error("Failed to re-approve.");
        });
      }
    });
  };

  const handleCancelOrder = (order: SaleOrder) => {
    Swal.fire({
      title: "Cancel this order?",
      text: "If you cancel this order, it cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel",
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#eee"
    }).then((result) => {
      if (result.isConfirmed) {
        WebService.putAPI({
          action: `/v1/door-management/saleorders/${order.sale_order_id}/status/CANCELED`,
        }).then(() => {
          toast.success("Order canceled!");
          refreshOrders();
        }).catch(() => {
          toast.error("Failed to cancel order.");
        });
      }
    });
  };

  /** Actions follow the order's own status, so they show on every tab (incl. ALL). */
  const renderRowActions = (order: SaleOrder) => {
    const status = String(order.status || "").toUpperCase();
    if (isEstimateStatus(status)) {
      return (
        <button
          className={styles.acceptBtn}
          title="Client confirmed the estimate: record payment or credit to place the order"
          onClick={ev => { ev.stopPropagation(); openProceedToPay(order); }}
        >
          Confirm / Pay
        </button>
      );
    }
    if (isPlacedStatus(status)) {
      return (
        <>
          <button
            className={styles.acceptBtn}
            onClick={ev => { ev.stopPropagation(); handleAccept(order); }}
          >
            Accept
          </button>
          <button
            className={styles.rejectBtn}
            onClick={ev => { ev.stopPropagation(); handleReject(order); }}
          >
            Reject
          </button>
        </>
      );
    }
    if (status === "REJECTED") {
      return (
        <>
          <button
            className={styles.acceptBtn}
            style={{ marginRight: 8 }}
            onClick={ev => { ev.stopPropagation(); handleReApprove(order); }}
          >
            Re-Approve
          </button>
          <button
            className={styles.rejectBtn}
            onClick={ev => { ev.stopPropagation(); handleCancelOrder(order); }}
          >
            Cancel
          </button>
        </>
      );
    }
    if (status === "PENDING" || status === "ACCEPTED") {
      return <span style={{ color: "#888", fontSize: 13 }}>Set delivery date to start production</span>;
    }
    return <span style={{ color: "#bbb" }}>—</span>;
  };

  // --- Popup for Assigning Employee ---
  const renderAssignPopup = () =>
    ReactDOM.createPortal(
      <div className={styles.overlay}>
        <div className={styles.assignModal}>
          <h2>Assign Employee to Order</h2>
         <div className={styles.modalField}>
  <label>Search Employee</label>

{selectedEmployee && !employeePickerOpen ? (
  <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:6 }}>
    {/* Clicking the chip re-opens the picker (replaces "Change") */}
    <button
      type="button"
      onClick={() => setEmployeePickerOpen(true)}
      style={{
        display:"inline-flex",
        alignItems:"center",
        gap:10,
        background:"#fff8cc",
        border:"1px solid #ffe08a",
        borderRadius:8,
        padding:"8px 12px",
        fontWeight:600,
        cursor:"pointer"
      }}
    >
      <span>{selectedEmployee.username}</span>
      <span style={{ color:"#777", fontWeight:400 }}>{selectedEmployee.mobile_number}</span>
    </button>

    {/* Cross icon instead of "Clear" */}
    <FaTimesCircle
      title="Clear"
      onClick={() => {
        setSelectedEmployeeId(null);
        setSelectedEmployee(null);
        setEmployeeSearch("");
        setEmployeePickerOpen(true);
      }}
      style={{ color:"#e43e2d", fontSize:20, cursor:"pointer" }}
    />
  </div>
) : (
    // Expanded view: search + results
    <>
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <FaSearch style={{ color:"#999" }} />
        <input
          type="text"
          value={employeeSearch}
          onChange={e => setEmployeeSearch(e.target.value)}
          onFocus={() => setEmployeePickerOpen(true)}
          placeholder="Type name or mobile..."
          style={{ flex:1, padding:"7px 8px", borderRadius:7, border:"1px solid #ddd" }}
          autoFocus
        />
      </div>

      {employeePickerOpen && (
        <div className={styles.employeeDropdown} style={{ marginTop: 8 }}>
          {employeeLoading ? (
            <div style={{ color:"#b4b4b4", padding:12, textAlign:"center" }}>
              Loading employees...
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div style={{ color:"#b4b4b4", padding:12, textAlign:"center" }}>
              No employees found
            </div>
          ) : (
          filteredEmployees.map(emp => (
            <div
              key={emp.user_id}
              className={selectedEmployeeId === emp.user_id ? styles.selectedEmployeeOption : styles.employeeOption}
              onClick={() => {
                setSelectedEmployeeId(emp.user_id);
                setSelectedEmployee(emp);
                setEmployeePickerOpen(false); // hide list after pick
              }}
              style={{
                background: selectedEmployeeId === emp.user_id ? "#ffd40033" : undefined,
                fontWeight: selectedEmployeeId === emp.user_id ? 700 : 500,
                color: selectedEmployeeId === emp.user_id ? "#232323" : "#222",
              }}
            >
              {emp.username} &nbsp;
              <span style={{ color:"#888", fontWeight:400 }}>{emp.mobile_number}</span>
            </div>
          ))
          )}
        </div>
      )}
    </>
  )}
</div>

          <div className={styles.modalField}>
            <label>Special Note (optional)</label>
            <textarea
              className={styles.modalTextarea}
              value={specialNote}
              placeholder="Write note for employee (optional)"
              onChange={e => setSpecialNote(e.target.value)}
              maxLength={250}
            />
          </div>
<div className={styles.modalFooter}>
  <button
    className={styles.primaryActionBtn}
    onClick={handleAssignSubmit}
    disabled={!selectedEmployeeId || assignLoading}
  >
    {assignLoading ? "Assigning..." : "Assign & Accept"}
  </button>

  <button
    className={styles.secondaryBtn}
    onClick={() => {
      setShowAssignPopup(false);
      setAssigningOrder(null);
      setSelectedEmployeeId(null);
      setSelectedEmployee(null);
      setSpecialNote("");
    }}
    disabled={assignLoading}
  >
    Cancel
  </button>
</div>

        </div>
      </div>,
      document.body
    );

  // --- Main Render ---
  return (
    <div className={styles.salesWrapper}>
      <div className={styles.pageTitle}>Sale Order Management</div>
      <div className={styles.headerRow}>
        <div className={styles.searchSection}>
          {/* Main Search */}
          <div className={styles.searchBox}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by username"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <FaTimesCircle
                className={styles.clearSearch}
                onClick={() => setSearch("")}
              />
            )}
          </div>
          {/* Mobile Number Search */}
          <div className={styles.searchBox}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              inputMode="numeric"
              className={styles.searchInput}
              placeholder="Search by mobile no"
              value={mobileSearch}
              onChange={e => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                setMobileSearch(v);
              }}
            />
            {mobileSearch && (
              <FaTimesCircle
                className={styles.clearSearch}
                onClick={() => setMobileSearch("")}
              />
            )}
          </div>
          {/* Order ID Search */}
          <div className={styles.orderIdSearchBox}>
            <input
              type="number"
              className={styles.orderIdInput}
              placeholder="Order ID..."
              value={orderIdSearch}
              onChange={e => {
                let v = e.target.value.replace(/[^0-9]/g, "");
                if (v.length > 15) v = v.slice(0, 15);
                setOrderIdSearch(v);
              }}
              min="1"
              step="1"
            />
            {orderIdSearch && (
              <FaTimesCircle
                className={styles.clearSearch}
                onClick={() => setOrderIdSearch("")}
                style={{ cursor: "pointer", marginLeft: -24 }}
              />
            )}
          </div>
          <div className={styles.dateFilters}>
            <input
              type="date"
              className={styles.dateInput}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              max={endDate || undefined}
              placeholder="Start Date"
            />
            <span style={{ margin: "0 5px" }}>to</span>
            <input
              type="date"
              className={styles.dateInput}
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              min={startDate || undefined}
              placeholder="End Date"
            />
            {(startDate || endDate) && (
              <button className={styles.clearDateBtn} onClick={() => { setStartDate(""); setEndDate(""); }}>
                Clear
              </button>
            )}
          </div>
        </div>
        <button className={styles.addOrderBtn} onClick={() => {
          setShowAddPanel(true);
          setSelectedOrderId(null);
          setSelectedUserId(null);
          setOpenedFromProceedToPay(false);
        }}>
          <span className={styles.addIcon}><MdAdd /></span> Add New Order
        </button>
      </div>

      <div className={styles.tabsRow}>
        {STATUS_TABS.map((tabKey) => (
          <button
            key={tabKey}
            className={`${styles.tabBtn} ${tab === tabKey ? styles.tabActive : ""}`}
            onClick={() => setTab(tabKey)}
          >
            {STATUS_LABELS[tabKey] || tabKey}
          </button>
        ))}
      </div>
      {selectedOrders.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            background: "#fffbe6",
            border: "1px solid #ffe08a",
            borderRadius: 10,
            padding: "10px 14px",
            margin: "10px 0",
          }}
        >
          <strong>{selectedOrders.length} selected</strong>
          {selectedClientCount > 1 && (
            <span style={{ color: "#b45309", fontSize: 13 }}>
              (from {selectedClientCount} different clients)
            </span>
          )}
          {selectedEstimateIds.length > 0 && (
            <button className={styles.acceptBtn} onClick={() => setBulkAction("CONFIRM_ESTIMATE")}>
              Confirm estimates ({selectedEstimateIds.length})
            </button>
          )}
          {selectedPlacedIds.length > 0 && (
            <button className={styles.acceptBtn} onClick={() => setBulkAction("ACCEPT")}>
              Accept orders ({selectedPlacedIds.length})
            </button>
          )}
          <button className={styles.secondaryBtn} onClick={() => setSelectedIds(new Set())}>
            Clear
          </button>
        </div>
      )}
      <div className={styles.tableWrapper}>
        {loading && orders.length === 0 ? (
          <div style={{ padding: 28, fontWeight: 600, color: "#FFD400" }}>Loading orders...</div>
        ) : apiError && orders.length === 0 ? (
          <div style={{ padding: 28, color: "#E13D22", fontWeight: 600 }}>{apiError}</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 36 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    title="Select all estimates / placed orders"
                    checked={allSelectableChecked}
                    disabled={selectableOrders.length === 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Order No</th>
                <th>Customer</th>
                <th>Order Date</th>
                {tab === "PLACED" && <th>Token Amount</th>}
                <th>Total Paid Amount</th>
                <th>Amount</th>
                <th>Due Amount</th>
                {tab === "PLACED" && <th>Image</th>}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
                        <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td
                    colSpan={tab === "PLACED" ? 11 : 9}
                    style={{
                      textAlign: "center",
                      color: "#bababa",
                      fontWeight: 600,
                      fontSize: 18,
                      padding: "36px 0"
                    }}
                  >
                    No Order Found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.sale_order_id}
                    style={{ cursor: "pointer" }}
                    className={styles.clickableRow}
                    onClick={() => navigate(`/saleorder/${order.sale_order_id}`)}
                  >
                    <td onClick={e => e.stopPropagation()}>
                      {isSelectable(order) && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(String(order.sale_order_id))}
                          onChange={() => toggleSelected(order)}
                        />
                      )}
                    </td>
                    <td>{order.order_no || order.sale_order_id}</td>
                    <td>{displayCustomerName(order)}</td>
                    <td>
                      {order.order_date && order.order_date !== "0001-01-01T00:00:00Z" && order.order_date !== ""
                        ? formatCreatedAt(order.order_date)
                        : order.created_at && order.created_at !== "0001-01-01T00:00:00Z" && order.created_at !== ""
                          ? formatCreatedAt(order.created_at)
                          : "—"}
                    </td>
                    {/* --- Token Amount for PLACED  --- */}
               {tab === "PLACED" && (
               <td>
               {(order.token_amount !== undefined && order.token_amount !== null)
            ? `${currency(order.token_amount)}${order.payment_method ? ` (${order.payment_method})` : ""}`
                : "₹0.00"}
             </td>
                 )}

                    <td>{currency((order as any).paid_amount)}</td>
                    <td>{currency((order as any).final_amount)}</td>
                    <td>{currency(order.due_amount)}</td>
                    {tab === "PLACED" && (
                      <td onClick={e => e.stopPropagation()} style={{ textAlign: "center" }}>
                        {order.payment_image ? (
                          <img
                            src={order.payment_image}
                            alt="Payment"
                            className={styles.paymentThumb}
                            title="Click to view full image"
                            style={{
                              width: 38,
                              height: 38,
                              objectFit: "cover",
                              borderRadius: 7,
                              boxShadow: "0 1px 6px #eee",
                              cursor: "pointer",
                              border: "1px solid #e1e1e1",
                            }}
                            onClick={ev => {
                              ev.stopPropagation();
                              window.open(order.payment_image, "_blank");
                            }}
                          />
                        ) : (
                          <span style={{ color: "#aaa", fontSize: 13 }}>No image</span>
                        )}
                      </td>
                    )}
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: STATUS_COLORS[order.status] || (isProgressStatus(order.status) ? STATUS_COLORS.default : "#bbb"),
                          color: order.status === "CHECKOUT" ? "#888" : "#fff",
                        }}
                      >
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>{renderRowActions(order)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        <div ref={loadMoreRef} className={styles.infiniteScrollSentinel} aria-hidden />
        {loadingMore && (
          <div className={styles.infiniteScrollLoading}>Loading more orders...</div>
        )}
        {!loading && !loadingMore && !hasMore && orders.length > 0 && (
          <div className={styles.infiniteScrollEnd}>
            Showing all {pagination?.total_records ?? orders.length} orders
          </div>
        )}
      </div>

      {/* --- Add Sale Order Panel  --- */}
      {showAddPanel &&
        ReactDOM.createPortal(
          (
            <div className={styles.overlay}>
              <div className={styles.slideInPanelWrapper}>
                <div ref={panelRef}>
                  <AddSaleOrderPanel
  open={showAddPanel}
  onClose={() => {
    setShowAddPanel(false);
    setSelectedOrderId(null);
    setSelectedUserId(null);
    setOpenedFromProceedToPay(false);
  }}
  saleOrderId={selectedOrderId ?? undefined}
  userId={selectedUserId ?? undefined}
  openedFromProceedToPay={openedFromProceedToPay}
/>

                </div>
              </div>
            </div>
          ),
          document.body
        )
      }

      {/* Assign Employee Modal */}
      {showAssignPopup && renderAssignPopup()}

      {/* Bulk confirm / accept */}
      {bulkAction && (
        <BulkConfirmModal
          action={bulkAction}
          saleOrderIds={bulkAction === "ACCEPT" ? selectedPlacedIds : selectedEstimateIds}
          onClose={() => setBulkAction(null)}
          onDone={refreshOrders}
        />
      )}
    </div>
  );
};

export default Sales;
